import * as _ from 'lodash';
import { Component, effect, inject, output, OutputEmitterRef, TemplateRef, viewChild, Signal } from '@angular/core';

import { CLAIM_PAGE } from '../../injection-tokens/PAGE-NAME';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { _TabPageService } from 'src/app/features/shared/central-page/services/_tab-page.service';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { LoadingPageService } from '../../services/loading-services/loading-page.service';
import { LogService } from '../../services/log.service';
import { _PageService } from '../../services/page-services/_page.service';
import { pageMode } from '../../models/page-mode';
import { sectionLayout, TSectionLayout } from '../../models/section-layout';
import { pageTransitionAnimation } from '../../animations/route-animations';
import { showAnimation } from '../../animations/show-animation';

@Component({
    selector: 'app-base-section',
    template: '',
    styles: [],
    animations: [
        pageTransitionAnimation,
        showAnimation,
    ],
})
export abstract class _SectionBaseComponent
{
    protected abstract readonly _pageService: _PageService;
    protected abstract readonly _centralPageService: _CentralPageService;
    protected abstract readonly _footerService: FooterService;
    protected abstract readonly _headerService: HeaderService;
    protected abstract readonly _loadingPageService: LoadingPageService;

    abstract out_onEscape: OutputEmitterRef<void>;
    abstract out_onPageLoaded: OutputEmitterRef<void>;

    abstract ngOnInit(): void;
    abstract emit_onEscape(): void;
    abstract emit_pageLoaded(): void;
    abstract ngAfterViewInit(): void;

    abstract template_footerLeft$$: Signal<TemplateRef<any> | undefined>;
    abstract template_sectionDetails_extraButtons$$: Signal<TemplateRef<any> | undefined>;
    abstract template_sectionEdit_extraButtons$$: Signal<TemplateRef<any> | undefined>;
    abstract footerData: Partial<IFooterData>;
    abstract headerData: Partial<IHeaderData>;

    abstract readonly sectionLayout$$: Signal<TSectionLayout>;

    protected _out_onEscape = output({alias: 'onEscape'});
    protected _out_onPageLoaded = output({alias: 'onPageLoaded'});

    protected _headerData_section: Partial<IHeaderData> | null = null;
    protected _footerData_section: Partial<IFooterData> | null = null;

    // De autorisatie-pagina van déze sectie (CLAIM_PAGE-token, in de eigen providers van het
    // concrete blad). Wordt door de subklasse omhoog geduwd naar _CentralPageService (details- of
    // edit-slot), waar de centrale page-chrome de knop-autorisatie berekent. Optioneel: features die
    // het token nog niet zetten leunen op de container (pageSection*_name) — coexistentie.
    protected readonly _claimPage = inject(CLAIM_PAGE, { optional: true });
    private readonly __tabPageService = inject(_TabPageService, { optional: true });

    protected _template_footerLeft$$ = viewChild<TemplateRef<any>>('template_footerLeft');
    protected _template_sectionDetails_extraButtons$$ = viewChild<TemplateRef<any>>('template_sectionDetails_extraButtons');
    protected _template_sectionEdit_extraButtons$$ = viewChild<TemplateRef<any>>('template_sectionEdit_extraButtons');

    constructor() {
        effect(() => this._centralPageService.sectionDetails_extraButtons_template_set(this.template_sectionDetails_extraButtons$$()));
        effect(() => this._centralPageService.sectionEdit_extraButtons_template_set(this.template_sectionEdit_extraButtons$$()));
        effect(() => this._footerService.template_buttonLeft_set(this._template_footerLeft$$()));
        effect(() => {
            this.__tabPageService?.sectionLayout_set(this.sectionLayout$$());
        });
    }

    protected _ngOnInit(): void {
        const _headerData_page = this._pageService.headerData_page$$();
        const _footerData_page = this._pageService.footerData_page$$();

        this._headerService.headerData_set({...this._headerData_section, ..._headerData_page, ...this.headerData}, true);
        this._footerService.footerData_set({...this._footerData_section, ..._footerData_page, ...this.footerData}, true);
    }

    protected _emit_onEscape(): void {
        LogService.envDev.logString('on keyDown escape section');

        this.out_onEscape.emit();
    }

    protected _emit_pageLoaded(): void {
        setTimeout(() => {
            this.out_onPageLoaded.emit();
        }, 0);
    }

    protected _ngAfterViewInit(): void {
        const _pageMode = this._centralPageService.pageMode$$();

        if (_pageMode == pageMode.createPage) {this._emit_pageLoaded();}
    }
}
